import { type CSSProperties, type JSX } from 'react';

import type { Tile as TileSchema } from '@schema/tiles.js';

import { Tile } from '../../Tile.js';

export interface HeroFeaturedProps {
  /**
   * Up to 4 featured tiles to render across the hero band. Spacer
   * tiles are filtered at the schema layer; this component renders
   * whatever the caller passes, in order.
   */
  featuredTiles: readonly TileSchema[];
  /**
   * Optional resolved URL of a Pro/Ent hero background image. When
   * set, it sits on top of the default slab color (CSS-managed via
   * --ml-header-bg) as a `cover` background with a slight gradient
   * overlay for tile legibility.
   */
  imageSrc?: string;
  /**
   * Dispatch handler — same one ProfileView gives to the main grid.
   * Featured tile taps fire the same `engine.dispatchTile` path so
   * the kiosk's existing failure UX (demo toast, agent errors) holds.
   */
  onTileActivate: (tile: TileSchema) => Promise<void> | void;
}

/**
 * Hero band — brand-colored slab with up to 4 featured tiles
 * horizontally centered on top.
 *
 * Background layering (bottom to top):
 *   1. Solid `backgroundColor` (always)
 *   2. Pro/Ent `imageSrc` covering the slab (optional)
 *   3. A subtle gradient overlay over the image so the tiles stay
 *      legible against light photography
 *
 * With zero featured tiles configured, the band collapses to just
 * the brand backdrop — useful as a profile-level visual identity
 * without any actionable content.
 */
export function HeroFeatured({
  featuredTiles,
  imageSrc,
  onTileActivate,
}: HeroFeaturedProps): JSX.Element {
  const style: CSSProperties | undefined =
    imageSrc !== undefined
      ? {
          // Gradient sits on top of the image so contrast against the
          // tiles is consistent regardless of customer photo content.
          backgroundImage:
            `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.30) 100%), url(${imageSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      : undefined;

  return (
    <section
      className={`launcher-hero launcher-hero--featured${
        imageSrc !== undefined ? ' launcher-hero--has-image' : ''
      }`}
      {...(style && { style })}
      data-testid="launcher-hero"
    >
      {featuredTiles.length > 0 && (
        <div className="launcher-hero__featured" data-testid="launcher-hero-featured">
          {featuredTiles.map((tile) => (
            <div key={tile.id} className="launcher-hero__featured-slot">
              <Tile tile={tile} onActivate={onTileActivate} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
